import React from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, KeyRound, Settings, Sparkles, Users } from 'lucide-react';
import { ApiService } from '../utils/api';
import { isEventId } from '../utils/eventRef';
import { resolveRoleUi } from '../utils/roleUi';

/** Event pages (registrations, access codes, analytics, settings) as separate pages, filtered by role. */
export const useEventPages = (eventRef, roles) => {
  const { data: resolved } = useQuery({
    queryKey: ['eventRef', eventRef],
    queryFn: () => ApiService.resolveEvent(eventRef),
    enabled: !!eventRef && !isEventId(eventRef),
    staleTime: Infinity,
  });
  const eventId = isEventId(eventRef) ? eventRef : resolved?.id;
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => ApiService.getEvent(eventId),
    enabled: !!eventId,
  });
  if (!eventRef || !event) return [];
  const ui = resolveRoleUi(event.roleUi, roles || []);
  const base = `/admin/events/${eventRef}`;
  return [
    ui.can('registrationsPage') && { to: `${base}/registrations`, label: 'Registrations', icon: Users },
    ui.can('accessCodesPage') && { to: `${base}/access-codes`, label: 'Access codes', icon: KeyRound },
    ui.can('analytics') && { to: `${base}/analytics`, label: 'Analytics', icon: BarChart3 },
    ui.isAdmin && { to: `${base}/settings`, label: 'Event settings', icon: Settings },
    ui.isAdmin && { to: `${base}/premium`, label: 'Premium registration', icon: Sparkles },
  ].filter(Boolean);
};

const EventNav = ({ pages, variant = 'panel', onNavigate }) => {
  if (pages.length < 2) return null;
  if (variant === 'sidebar') {
    return (
      <div className="ml-6 mt-1 space-y-1">
        {pages.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) => `group flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
          >
            <Icon className="mr-3 h-4 w-4" />
            {label}
          </NavLink>
        ))}
      </div>
    );
  }
  return (
    <nav aria-label="Event pages" className="space-y-1 px-3 py-4">
      {pages.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          className={({ isActive }) => `flex items-center rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
            isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
        >
          <Icon className="mr-3 h-5 w-5" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
};

export default EventNav;
